import { useRef } from "react";
import { motion, useInView } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

// Masked line-by-line reveal. `lines` = array of strings, or array of
// arrays of {t, em} parts for mixed serif-italic emphasis.
export function MaskedLines({ lines, className = "", delay = 0, stagger = 0.09, trigger = "inView", active = false }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -8% 0px", amount: 0.01 });
  const show = trigger === "mount" ? true : trigger === "signal" ? active : inView;
  return (
    <span ref={ref} className={className}>
      {lines.map((line, i) => {
        return (
          <span key={i} className="block overflow-hidden pb-[0.12em]">
            <motion.span
              className="block"
              initial={{ y: "110%" }}
              animate={{ y: show ? "0%" : "110%" }}
              transition={{ duration: 0.9, ease: EASE, delay: delay + i * stagger }}
            >
              {Array.isArray(line)
                ? line.map((p, j) =>
                  p.em ? (
                    <span key={j} className={`emph ${p.italic ? "italic" : ""}`}>
                      {p.t}
                    </span>
                  ) : (
                    <span key={j} className={p.italic ? "italic" : ""}>{p.t}</span>
                  )
                )
              : line}
          </motion.span>
          </span>
        );
      })}
    </span>
  );
}

export function FadeUp({ children, className = "", delay = 0, y = 28, once = true }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-8% 0px" }}
      transition={{ duration: 0.8, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

export function Stagger({ children, className = "", delayChildren = 0, stagger = 0.08, once = true }) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once, margin: "-8% 0px" }}
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: stagger, delayChildren } },
      }}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className = "", y = 24 }) {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y },
        show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
      }}
    >
      {children}
    </motion.div>
  );
}

export { EASE };
